// src/components/PairCard.tsx
import React, { useState, useEffect } from "react";
import type { AnalyzePairResponse } from "../api/myTypes";
import { buildApiUrl } from "../api/etfApi";
import TickerPrice from "./TickerPrice";

interface PairCardProps {
  data: AnalyzePairResponse;
}

const PRICE_REFRESH_MS = 30000; // Same as Auto Cycle (30s) so both strategies refresh the same way

const PairCard: React.FC<PairCardProps> = ({ data }) => {
  const dt = new Date(data.generated_at);
  const [marketStatus, setMarketStatus] = useState<string>("Market Closed");

  // Fallback prices from analysis candles (same source as backend: Polygon then Yahoo)
  let primaryFromCandles: number | null = null;
  let primaryPrevFromCandles: number | null = null;
  let secondaryFromCandles: number | null = null;
  let secondaryPrevFromCandles: number | null = null;
  if (data.pair_profile?.primary_profile?.price_context?.candles) {
    const candles = data.pair_profile.primary_profile.price_context.candles;
    if (candles.length > 0) {
      primaryFromCandles = candles[candles.length - 1]?.close || null;
      if (candles.length > 1) primaryPrevFromCandles = candles[candles.length - 2]?.close || null;
    }
  }
  if (data.pair_profile?.secondary_profile?.price_context?.candles) {
    const candles = data.pair_profile.secondary_profile.price_context.candles;
    if (candles.length > 0) {
      secondaryFromCandles = candles[candles.length - 1]?.close || null;
      if (candles.length > 1) secondaryPrevFromCandles = candles[candles.length - 2]?.close || null;
    }
  }

  // Live price from same endpoint as Auto Cycle (api/current-price = yfinance) so display matches
  const [primaryCurrentPrice, setPrimaryCurrentPrice] = useState<number | null>(primaryFromCandles);
  const [primaryPreviousPrice, setPrimaryPreviousPrice] = useState<number | null>(primaryPrevFromCandles);
  const [secondaryCurrentPrice, setSecondaryCurrentPrice] = useState<number | null>(secondaryFromCandles);
  const [secondaryPreviousPrice, setSecondaryPreviousPrice] = useState<number | null>(secondaryPrevFromCandles);

  // Sync fallback from candles when data changes (e.g. new analysis)
  useEffect(() => {
    setPrimaryCurrentPrice((p) => p ?? primaryFromCandles);
    setPrimaryPreviousPrice((p) => p ?? primaryPrevFromCandles);
    setSecondaryCurrentPrice((p) => p ?? secondaryFromCandles);
    setSecondaryPreviousPrice((p) => p ?? secondaryPrevFromCandles);
  }, [primaryFromCandles, primaryPrevFromCandles, secondaryFromCandles, secondaryPrevFromCandles]);

  // Poll live price from same endpoint as Auto Cycle (30s) so both strategies refresh the same way
  useEffect(() => {
    const primaryTicker = data.primary_ticker;
    const secondaryTicker = data.secondary_ticker;

    const fetchPrice = async (ticker: string, isPrimary: boolean) => {
      try {
        const url = buildApiUrl(`api/current-price/${ticker}`);
        const response = await fetch(url);
        if (!response.ok) return;
        const priceData = await response.json();
        if (!priceData.current_price) return;
        const current = Number(priceData.current_price);
        const previousClose = priceData.previous_close != null ? Number(priceData.previous_close) : null;

        if (isPrimary) {
          setPrimaryCurrentPrice(current);
          if (previousClose != null && previousClose > 0) {
            setPrimaryPreviousPrice((prev) =>
              prev == null || Math.abs(prev - previousClose) > 0.01 ? previousClose : prev
            );
          }
        } else {
          setSecondaryCurrentPrice(current);
          if (previousClose != null && previousClose > 0) {
            setSecondaryPreviousPrice((prev) =>
              prev == null || Math.abs(prev - previousClose) > 0.01 ? previousClose : prev
            );
          }
        }
      } catch (err) {
        console.error(`PairCard: error fetching price for ${ticker}`, err);
      }
    };

    fetchPrice(primaryTicker, true);
    fetchPrice(secondaryTicker, false);
    const interval = setInterval(() => {
      fetchPrice(primaryTicker, true);
      fetchPrice(secondaryTicker, false);
    }, PRICE_REFRESH_MS);
    return () => clearInterval(interval);
  }, [data.primary_ticker, data.secondary_ticker]);

  // Update market status on mount and periodically
  useEffect(() => {
    const getMarketStatus = () => {
      const now = new Date();
      const etTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
      const dayOfWeek = etTime.getDay(); // 0 = Sunday, 6 = Saturday
      const hours = etTime.getHours();
      const minutes = etTime.getMinutes();
      const timeInMinutes = hours * 60 + minutes;
      
      // Markets are closed on weekends (Saturday = 6, Sunday = 0)
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return "Market Closed";
      }
      
      // Pre-market: 4:00 AM - 9:30 AM ET (240-570 minutes) - Monday through Friday only
      // Regular market: 9:30 AM - 4:00 PM ET (570-960 minutes) - Monday through Friday only
      // Post-market: 4:00 PM - 8:00 PM ET (960-1200 minutes) - Monday through Friday only
      if (timeInMinutes >= 240 && timeInMinutes < 570) {
        return "Pre-Market";
      } else if (timeInMinutes >= 570 && timeInMinutes < 960) {
        return "Market Open";
      } else if (timeInMinutes >= 960 && timeInMinutes < 1200) {
        return "Post-Market";
      } else {
        return "Market Closed";
      }
    };

    const updateMarketStatus = () => {
      const status = getMarketStatus();
      setMarketStatus(status);
    };

    // Initial update
    updateMarketStatus();

    // Update every minute to catch status changes
    const statusInterval = setInterval(updateMarketStatus, 60000);

    return () => {
      clearInterval(statusInterval);
    };
  }, []); // Empty dependency array - only run on mount/unmount

  return (
    <div className="card pair-card">
      <div className="card-header-row">
        <div>
          <div className="pair-tickers">
            <span className="ticker primary">
              {data.primary_ticker}
              <TickerPrice 
                currentPrice={primaryCurrentPrice} 
                previousPrice={primaryPreviousPrice} 
              />
            </span>
            <span className="pair-separator">↔</span>
            <span className="ticker secondary">
              {data.secondary_ticker}
              <TickerPrice 
                currentPrice={secondaryCurrentPrice} 
                previousPrice={secondaryPreviousPrice} 
              />
            </span>
          </div>
          <div 
            style={{
              fontSize: "0.65rem",
              color: marketStatus === "Market Open" ? "#22c55e" : 
                     marketStatus === "Pre-Market" ? "#f59e0b" :
                     marketStatus === "Post-Market" ? "#3b82f6" : "#6b7280",
              fontWeight: 500,
              marginTop: "4px",
              marginBottom: "2px",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              display: "block",
              lineHeight: "1.2",
              minHeight: "14px",
              visibility: "visible",
              opacity: 1,
              width: "100%"
            }}
            data-testid="market-status-indicator"
          >
            {marketStatus || "Market Closed"}
          </div>
          <div className="pair-horizon">
            Horizon: {data.time_horizon_days} days
          </div>
        </div>
      </div>
      <div className="card-footer-row">
        <span className="meta-muted">
          Generated {dt.toLocaleString(undefined, { hour12: false })}
        </span>
      </div>
    </div>
  );
};

export default PairCard;
